import {Injectable} from '@angular/core';
import * as moment from 'moment';
import {Moment} from 'moment';
import {BehaviorSubject} from 'rxjs';
import * as _ from 'lodash';
import {Platform} from '@ionic/angular';

export interface Mes {
  month: Moment;
  date: Date;
  monthName: string;
  weeks?: any[];
}

export interface Periodo {
  start: Dia;
  end: Dia;
}

export interface Dia {
  date: Date;
  monthDay: number;
  weekDay: number;
  isToday?: boolean;
  isMonthCurrent?: boolean;
  selected?: boolean;
  active?: boolean;
  start?: boolean;
  end?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CalendarioService {
  private meses: Mes[] = [];
  private meses$: BehaviorSubject<Mes[]> = new BehaviorSubject<Mes[]>(this.meses);
  private start: Dia;
  private end: Dia;
  private periodo$: BehaviorSubject<Periodo> = new BehaviorSubject<Periodo>(null);
  pagination = 1;

  constructor(private platform: Platform) {
    const calendarSize = this.getCalendarSize(this.platform.width());
    this.pagination = Math.trunc(this.platform.width() / calendarSize);
    this.platform.resize.asObservable().subscribe(() => {
      const amount = Math.trunc(this.platform.width() / this.getCalendarSize(this.platform.width()));
      if (amount !== this.pagination) {
        this.pagination = amount;
        this.meses = this.getMonths(moment(), this.pagination);
        this.meses$.next(this.meses);
      }
    });
    this.meses = this.getMonths(moment(), this.pagination);
    this.meses$.next(this.meses);
  }

  getCalendarSize(width: number) {
    let calendarSize = 293;
    if (width >= 1200) {
      calendarSize = 293 + 30;
    }
    return calendarSize;
  }

  getPeriodo() {
    return this.periodo$.asObservable();
  }

  setPeriodo() {
    this.periodo$.next({
      start: this.start,
      end: this.end
    });
  }

  getMeses() {
    return this.meses$.asObservable();
  }

  private getMonths(start: Moment, pagination = 1) {
    const meses: Mes[] = [];
    const firstMonth = start;
    meses.push({
      month: firstMonth,
      date: firstMonth.toDate(),
      monthName: firstMonth.locale('pt-br').format('MMMM')
    });
    for (let i = 1; i < pagination; i++) {
      const mes = firstMonth.clone().add(i, 'M');
      meses.push({
        month: mes,
        date: mes.toDate(),
        monthName: mes.locale('pt-br').format('MMMM')
      });
    }

    for (const mes of meses) {
      mes.weeks = [];
      const lastOfMonth = mes.month.clone().endOf('month');
      const firstOfMonth = mes.month.clone().startOf('month');
      const currentWeek = firstOfMonth.clone().day(0);
      let startOfWeek = null;
      let endOfWeek = null;
      let date = null;
      while (currentWeek < lastOfMonth) {
        const dias = [];
        startOfWeek = currentWeek.clone().day(0);
        endOfWeek = currentWeek.clone().day(6);
        date = startOfWeek.clone();
        for (let w = 0; w <= 6; w++) {
          date.day(w);
          const dia = {
            date: moment(date).toDate(),
            monthDay: date.date(),
            weekDay: date.day(),
            isToday: false,
            isMonthCurrent: true
          };
          if (moment().isSame(date, 'd')) {
            dia.isToday = true;
          }
          if (!moment(mes.month).isSame(date, 'M')) {
            dia.isMonthCurrent = false;
          }
          if (this.start && this.end) {
            this.selecionarDia(dia);
          }
          dias.push(dia);
        }
        currentWeek.day(7);
        mes.weeks.push(dias);
      }
    }
    return meses;
  }

  private selecionarDia(dia) {
    dia.active = false;
    dia.start = false;
    dia.end = false;
    dia.selected = moment(dia.date).isBetween(this.start.date, this.end.date) && dia.isMonthCurrent;
    if (moment(dia.date).isSame(moment(this.start.date)) && dia.isMonthCurrent) {
      dia.active = true;
      dia.start = true;
    }
    if (moment(dia.date).isSame(moment(this.end.date)) && dia.isMonthCurrent) {
      dia.active = true;
      dia.end = true;
    }
  }

  selecionar(day: Dia) {
    if (this.start && this.end && (moment(day.date).isSame(this.start.date) || moment(day.date).isSame(this.end.date))) {
      return;
    }
    if (!this.start) {
      this.start = _.clone(day);
    }
    if (moment(day.date).isBefore(this.start.date)) {
      this.start = _.clone(day);
    } else {
      this.end = _.clone(day);
    }
    this.fillMonths();
    this.setPeriodo();
  }

  fillMonths(isFill = true) {
    for (const mes of this.meses) {
      for (const semana of mes.weeks) {
        for (const dia of semana) {
          if (isFill) {
            this.selecionarDia(dia);
          } else {
            this.deselecionar(dia);
          }
        }
      }
    }
  }

  voltarMes() {
    this.meses = this.getMonths(this.meses.shift().month.clone().subtract(this.pagination, 'M'), this.pagination);
    this.meses$.next(this.meses);
  }

  avancarMes() {
    this.meses = this.getMonths(this.meses.shift().month.clone().add(this.pagination, 'M'), this.pagination);
    this.meses$.next(this.meses);
  }

  voltarDia(isEnd: boolean = false) {
    let dia = this.start;
    if (isEnd) {
      dia = this.end;
    }
    dia.date = moment(dia.date).subtract(1, 'd').toDate();
    const index = this.meses.indexOf(this.meses.find(x => x.month.isSame(dia.date, 'M')));
    const firstMonth = this.meses[0].month;
    if (!firstMonth.isSame(dia.date, 'M') && index <= 0) {
      this.meses = this.getMonths(moment(dia.date), this.pagination);
      this.meses$.next(this.meses);
    } else {
      this.fillMonths();
    }
    this.setPeriodo();
  }

  avancarDia(isEnd: boolean = false) {
    let dia = this.start;
    if (isEnd) {
      dia = this.end;
    }
    dia.date = moment(dia.date).add(1, 'd').toDate();
    const index = this.meses.indexOf(this.meses.find(x => x.month.isSame(dia.date, 'M')));
    const lastMonth = this.meses[this.meses.length - 1].month;
    if (!lastMonth.isSame(dia.date, 'M') && index === -1) {
      this.meses = this.getMonths(moment(dia.date), this.pagination);
      this.meses$.next(this.meses);
    } else {
      this.fillMonths();
    }
    this.setPeriodo();
  }

  resetar() {
    this.start = undefined;
    this.end = undefined;
    this.periodo$.next(null);
    this.fillMonths(false);
  }

  deselecionar(dia) {
    dia.active = false;
    dia.start = false;
    dia.end = false;
    dia.selected = false;
  }

  setPagination(width) {
    this.pagination = Math.trunc(width / this.getCalendarSize(width));
    this.meses = this.getMonths(moment(), this.pagination);
    this.meses$.next(this.meses);
  }
}
